import React, { useState } from 'react';

/**
 * Stakeholder Analysis Guidance Component
 * Provides specialized guidance for stakeholder/context analysis tasks
 */
function StakeholderGuidance({ task, fileInputRef, uploading, files, onFileUpload }) {
  const [showUpload, setShowUpload] = useState(false);
  const hasFiles = files && files.length > 0;

  const stakeholderTypes = [
    { type: 'Kunder', examples: 'Sluttbrukere, innkjøpere, kontraktsparter', icon: '👥' },
    { type: 'Ansatte', examples: 'Fast ansatte, vikarer, lærlinger', icon: '👷' },
    { type: 'Leverandører', examples: 'Råvarer, tjenester, utstyr', icon: '🚚' },
    { type: 'Myndigheter', examples: 'Arbeidstilsynet, Mattilsynet, kommunen', icon: '🏛️' },
    { type: 'Eiere/investorer', examples: 'Aksjonærer, styret, morselskap', icon: '💼' },
    { type: 'Samfunnet', examples: 'Naboer, media, interesseorganisasjoner', icon: '🌍' },
  ];

  const analysisQuestions = [
    'Hvem påvirkes av det vi gjør?',
    'Hvem kan påvirke oss?',
    'Hva forventer de av oss?',
    'Hvordan kommuniserer vi med dem?',
    'Hvordan måler vi om de er fornøyde?',
  ];

  return (
    <div data-testid="stakeholder-guidance">
      {/* Why this is important */}
      <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-purple-900 mb-1">Hvem påvirker virksomheten din?</p>
            <p className="text-sm text-purple-800">
              Forståelse av interessepartneres behov og forventninger sikrer at styringssystemet 
              dekker de rette områdene. Dette er et krav i ISO-standardene (kapittel 4).
            </p>
          </div>
        </div>
      </div>

      {/* Stakeholder types */}
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
        Typiske interesseparter
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        {stakeholderTypes.map((stakeholder, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">{stakeholder.icon}</span>
              <span className="font-medium text-gray-900 text-sm">{stakeholder.type}</span>
            </div>
            <p className="text-xs text-gray-500">{stakeholder.examples}</p>
          </div>
        ))}
      </div>

      {/* Analysis questions */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
        <h4 className="font-semibold text-blue-900 mb-3">Spørsmål å stille for hver interessepart</h4>
        <ul className="space-y-2">
          {analysisQuestions.map((q, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-blue-800">
              <span className="text-blue-500 mt-0.5">{idx + 1}.</span>
              {q}
            </li>
          ))}
        </ul>
      </div>

      {/* Output hint */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
        <h4 className="font-semibold text-green-900 mb-2">Dokumentasjon</h4>
        <p className="text-sm text-green-800">
          Lag en enkel tabell med: Interessepart → Krav/forventninger → Hvordan vi oppfyller disse
        </p>
      </div>

      {/* Upload option */}
      <div className="border border-gray-200 rounded-xl p-4">
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="w-full flex items-center gap-3 text-left"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <span className="font-medium text-gray-900">Last opp interessepartanalyse</span>
            <p className="text-sm text-gray-500 mt-0.5">
              Har du allerede dokumentert dette?
            </p>
          </div>
          <svg className={`w-5 h-5 text-gray-400 transition-transform ${showUpload ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showUpload && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => fileInputRef?.current?.click()}
              disabled={uploading}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Laster opp...' : 'Last opp dokument'}
            </button>
            {hasFiles && (
              <p className="text-xs text-green-600 mt-2 text-center">
                ✓ {files.length} dokument(er) lastet opp
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default StakeholderGuidance;
